import React, { type ReactNode, type ForwardedRef, forwardRef, useRef, useImperativeHandle, type ReactElement, cloneElement, useMemo } from "react";
import { twMerge, twJoin } from "tailwind-merge";

import { SupPopover } from "../../../components/UI/Popover";
import { SupText } from "../../../components/UI/Text";
import { SupScrollable } from "../../../components/UI/Scrollable";
import { SupChevronDown } from "../../../icons";
import { useControlledState } from "../../../hooks/useControlledState";
import { useResizeObserver } from "../../../hooks/useResizeObserver";
import { CommonColors } from "../../../types/colors";

import { type OptionProps, type Option, SupOption, defaultLabelRenderer } from "./components/Option";

export interface SelectProps<V = unknown> {
  id?: string;
  name?: string;
  disabled?: boolean;
  defaultOpen?: boolean;
  open?: boolean;
  defaultValue?: V;
  value: V;
  children: ReactElement<OptionProps<V>, typeof SupOption>[];
  placeholder?: string;
  className?: string;
  optionClassName?: string;
  popoverClassName?: string;
  popoverContentClassName?: string;
  color?: CommonColors;
  onSelect: (value: V) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onOutsideClick?: () => void;
  renderSelectedOption?: (option: Option<V>) => ReactNode;
  renderOptionLabel?: (option: Option<V>, active: boolean) => ReactNode;
  renderOptionIcon?: (option: Option<V>, active: boolean) => ReactNode;
}

const colors = {
  primary: "border-0.5 border-neutral-500 enabled:hover:border-neutral-700 enabled:focus:border-neutral-500 enabled:active:border-neutral-800",
  success: "border-0.5 border-success-900",
  info: "border-0.5 border-info-800",
  error: "border-0.5 border-error-925",
  warning: "border-0.5 border-warning-900",
} as const;

export * from "./components/Option";

function Select<V>(
  {
    id,
    name,
    className,
    optionClassName,
    popoverClassName,
    popoverContentClassName,
    open,
    defaultOpen,
    value,
    defaultValue,
    color = "primary",
    disabled = false,
    placeholder,
    children,
    onOpen,
    onClose,
    onSelect,
    onFocus,
    onBlur,
    onOutsideClick,
    renderOptionIcon,
    renderOptionLabel = defaultLabelRenderer,
    renderSelectedOption = option => renderOptionLabel(option, true),
  }: SelectProps<V>,
  ref: ForwardedRef<HTMLDivElement>
) {
  const elRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useControlledState({ value: open, defaultValue: defaultOpen, onStateChange: open => (open ? onOpen?.() : onClose?.()) });
  const [selectedValue, setSelectedValue] = useControlledState({ value, defaultValue, onStateChange: onSelect });
  const { width } = useResizeObserver(elRef.current);

  useImperativeHandle<HTMLDivElement | null, HTMLDivElement | null>(ref, () => elRef.current, []);

  const optionMap = useMemo(() => new Map(children.map(option => [option.props.value, { label: option.props.label, value: option.props.value }])), [children]);
  const selectedOption = optionMap.get(selectedValue as V);

  function togglePopover() {
    setIsOpen(!isOpen);
  }

  function closePopover() {
    setIsOpen(false);
  }

  function handleSelecElement(value: unknown) {
    closePopover();
    setSelectedValue(value as V);
  }

  function handleOutsideClick() {
    closePopover();
    onOutsideClick?.();
  }

  return (
    <>
      <div
        ref={elRef}
        tabIndex={!disabled ? 0 : -1}
        className={twMerge(
          "sup-select relative inline-flex w-full items-center h-[3.25rem] pl-4 pr-9 border-0.5 transition-colors duration-200 bg-neutral-50 hover:bg-neutral-75 focus:bg-neutral-50 active:hover:bg-neutral-75 [&.disabled]:bg-neutral-50 [&.disabled]:border-neutral-100",
          disabled ? "disabled pointer-events-none" : "cursor-pointer",
          colors[color],
          className
        )}
        onClick={togglePopover}
        onFocus={onFocus}
        onBlur={onBlur}
      >
        {!selectedOption && Boolean(placeholder) && (
          <SupText as="span" variant="caption" className="text-neutral-600 select-none whitespace-nowrap max-w-full overflow-hidden">
            {placeholder}
          </SupText>
        )}

        {selectedOption ? renderSelectedOption(selectedOption) : null}

        <SupChevronDown
          className={twJoin(
            "absolute right-4 text-neutral-600 transition-transform duration-200",
            disabled ? "text-neutral-100" : "text-neutral-600",
            isOpen && !disabled ? "rotate-180" : "rotate-0"
          )}
        />

        <input id={id} name={name} type="hidden" value={(typeof selectedValue !== "object" ? selectedValue : JSON.stringify(selectedValue)) as string} />
      </div>

      <SupPopover
        className={twJoin("shadow", popoverClassName)}
        open={isOpen && !disabled}
        portal
        anchorEl={elRef.current}
        onClickOutside={handleOutsideClick}
        style={{ width }}
        offset={[0, 4]}
      >
        <SupScrollable className={twMerge("custom-scrollbar max-h-[19.5rem]", popoverContentClassName)}>
          {children.map(option =>
            cloneElement(option, {
              active: option.props.value === selectedValue,
              onSelect: handleSelecElement,
              className: optionClassName,
              renderIcon: renderOptionIcon,
              renderLabel: renderOptionLabel,
            })
          )}
        </SupScrollable>
      </SupPopover>
    </>
  );
}

export const SupSelect = forwardRef(Select) as <V>(props: SelectProps<V> & { ref?: React.ForwardedRef<HTMLDivElement> }) => ReturnType<typeof Select<V>>;
